import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The "How Fashion Leaders Are Preparing the Shopping Season" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/ai-in-fashion-retail/`), sent as four screenshots on
 * 2026-08-28 with *"referans alarak ilgili sayfayı tastamam şekilde, aynısını,
 * tıpkısını tasarla"*. Band for band that page's: the two-column hero with the
 * shopping photograph beside it, the grey "Why Download?" card with the stacked
 * copies and two paragraphs, the "What You'll Learn" card with the single
 * mockup and five ticks, the centred "Highlights" card, and the centred closing
 * ask. The copy is that page's copy, verbatim.
 *
 * Fifth e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation and Measurement, and it follows `ebook-measurement.ts` in every
 * place the reference and this site disagree — the same three deliberate
 * departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned label. Every other hero here is that
 *    shape.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean
 *    the page's only call to action silently drops every lead. The `check`
 *    below says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for again on 2026-08-28
 *    (*"en aşağıda önceden konuştuğumuz gibi testimonial template ini
 *    kullanalım"*). The card is LC Waikiki's: of the twenty in
 *    `content/testimonials.ts` it is the only **fashion retailer**, and the one
 *    talking about maturity differing across retail, logistics and e-commerce —
 *    which is this book's own subject. Both its logo and Şerafettin Özer's
 *    portrait are already in the repository from the Hall of Fame, so nothing
 *    better needed inventing. Its words are the Hall of Fame's; that file is
 *    where a change to them belongs.
 *
 * **The slug is confirmed, and it is not the one the catalogue derived.**
 * `content/ebooks.ts` guessed `how-fashion-leaders-are-preparing-the-shopping-season`
 * from the title; the screenshots came from
 * `digitopia.co/impact-labs/ebooks/ai-in-fashion-retail/`, so the real address
 * is the short one. The catalogue entry was corrected to match, which also
 * closed that e-book's slug `check` — the third time the title rule has been
 * caught out, after Sustainability Innovation and Measurement.
 */
export const ebookAiInFashionRetail: WrittenPage = {
  navLabel: 'AI in Fashion Retail',
  title: 'Lead the Shopping Season with AI in Fashion Retail',
  description:
    'Abercrombie & Fitch, Lululemon and Pandora are already reshaping the season. See how fashion retail leaders turn digital investment into measurable performance. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'How Retail Leaders Are Preparing the Season',
      headline: 'Lead the Shopping Season with AI in Fashion Retail',
      body: [
        'Abercrombie & Fitch, Lululemon, and Pandora are already reshaping the season.',
        'Discover how fashion retail leaders are turning digital investments into measurable '
          + 'performance. See what they’re doing differently and how you can catch up.',
      ],
      ctas: [{ label: 'Download', href: LINKS.fashionRetailEbookPdf }],
      photo: {
        src: '/photo/ai-fashion-retail-shopping.webp',
        alt:
          'A shopper in sunglasses carrying paper and fabric bags, photographed from below '
          + 'against the glass façade of a shopping centre at dusk.',
      },
      // The photograph is square and the site's hero box is 3:2, which would
      // take the top and bottom off her.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'shopping photograph and the two cover mockups anybody has sent; every '
        + 'other hero on the site is 2560px, so this one is painted at roughly its '
        + 'own width on a laptop with nothing left over for a retina screen. If '
        + 'bigger exports exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/ai-fashion-retail-shopping.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download?',
      body: [
        'The shopping season defines the year for fashion retailers. But success isn’t just '
          + 'about new collections or polished supply chains.',
        'Today, AI in fashion retail is shaping both content and commerce, powering fulfilment, '
          + 'and upskilling teams. Leaders are already operationalising what others are still '
          + 'piloting.',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.fashionRetailEbookPdf }],
      photo: {
        src: '/photo/ai-fashion-retail-ebook-covers.webp',
        alt:
          'Three copies of the fashion retail e-book, one behind the other, on a white '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      sub: 'Inside this short analysis, you’ll discover:',
      points: [
        'How AI is influencing everything from marketing copy to product fit assistants',
        'Why omnichannel fulfilment is now a brand differentiator, not a nice-to-have',
        'How loyalty and first-party data are fuelling real-time personalisation',
        'How frontline teams are being digitally trained and empowered',
        'How leading brands are building operating models around digital, not on top of it',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.fashionRetailEbookPdf }],
      photo: {
        src: '/photo/ai-fashion-retail-ebook-cover.webp',
        alt:
          'The fashion retail e-book standing upright, its cover on a black and pink '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Highlights',
      sub: 'In the report, you’ll see how:',
      points: [
        'Abercrombie & Fitch is using weather and social signals for demand forecasting',
        'Lululemon is training staff through digital certifications',
        'Pandora is rolling out AI-driven campaigns at unprecedented speed',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.fashionRetailEbookPdf }],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Prepare Your Brand for Peak Season with AI-Driven Strategies',
      sub:
        'See how fashion and luxury leaders are preparing for the shopping season, then '
        + 'benchmark your own readiness. Download the playbook to see the full strategies.',
      cta: { label: 'Download the Ebook', href: LINKS.fashionRetailEbookPdf },
    },
    {
      // The reference carries this line under its form, and it is the reason the
      // three brand names can be on the page at all. It stays.
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'This report is for informational and benchmarking purposes only. Digitopia is not '
        + 'affiliated with or endorsed by the brands mentioned.',
      ],
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's LC Waikiki card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'Strategic prioritization has been a key focus for us since 2021. With varying '
            + 'maturity levels across our business units—retail, logistics, and e-commerce—'
            + 'alignment is crucial. Conducting the assessment with full transparency helps us '
            + 'stay on track and make informed decisions.',
          source: 'Şerafettin Özer, Chief Digital and Information Officer',
          company: 'LC Waikiki',
          logo: { name: 'LC Waikiki', src: '/logo/lcwaikikicard.png' },
          portrait: { src: '/photo/portrait-serafettin-ozer.png', alt: 'Şerafettin Özer' },
        },
      ],
    },
  ],
}
